import { FormEvent, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ApiError, setAccessToken } from "@/api/client";
import {
  confirmMfaWithToken,
  completeOidcMfa,
  fetchMe,
  listOidcProviders,
  setupMfaWithToken,
  startOidc,
} from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";
import { defaultLandingPath } from "@/auth/permissions";

type OidcProvider = { key: string; label: string; enabled: boolean };

export function LoginPage() {
  const { user, login, acceptSession } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("producer@claims.local");
  const [password, setPassword] = useState("ChangeMe123!");
  const [mfaCode, setMfaCode] = useState("");
  const [needMfaCode, setNeedMfaCode] = useState(false);
  const [enrollmentToken, setEnrollmentToken] = useState<string | null>(null);
  const [provisioningUri, setProvisioningUri] = useState<string | null>(null);
  const [enrollCode, setEnrollCode] = useState("");
  const [oidcMfaToken, setOidcMfaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [oidcProviders, setOidcProviders] = useState<OidcProvider[]>([]);

  useEffect(() => {
    listOidcProviders()
      .then((data) => setOidcProviders(data.providers))
      .catch(() => setOidcProviders([]));
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const params = new URLSearchParams(hash);

    const oidcError = new URLSearchParams(window.location.search).get("oidc_error");
    if (oidcError) {
      setError(decodeURIComponent(oidcError));
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    const enrollment = params.get("enrollment_token");
    if (enrollment) {
      setEnrollmentToken(enrollment);
      setEmail(params.get("email") || email);
      (async () => {
        try {
          const setup = await setupMfaWithToken(enrollment);
          setProvisioningUri(setup.provisioning_uri);
        } catch {
          setError("SSO sign-in requires MFA enrollment. Failed to start setup.");
        }
      })();
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    const oidcMfa = params.get("oidc_mfa_token");
    if (oidcMfa) {
      setOidcMfaToken(oidcMfa);
      setEmail(params.get("email") || email);
      setNeedMfaCode(true);
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    const access = params.get("access_token");
    if (!access) return;
    (async () => {
      try {
        // Refresh token arrives via httpOnly cookie set by the OIDC callback.
        setAccessToken(access);
        const profile = await fetchMe();
        acceptSession(access, profile);
        window.history.replaceState(null, "", window.location.pathname);
      } catch {
        setError("SSO sign-in failed. Your email may not be invited to an agency yet.");
      }
    })();
  }, [acceptSession, email]);

  if (user) {
    const target = (location.state as { from?: string } | null)?.from ?? defaultLandingPath(user);
    return <Navigate to={target} replace />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (oidcMfaToken) {
        const session = await completeOidcMfa(oidcMfaToken, mfaCode);
        acceptSession(session.access_token, session.user);
        setOidcMfaToken(null);
        return;
      }
      await login(email, password, mfaCode || undefined);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403 && err.payload?.code === "mfa_enrollment_required") {
        const token = String(err.payload.enrollment_token || "");
        setEnrollmentToken(token);
        try {
          const setup = await setupMfaWithToken(token);
          setProvisioningUri(setup.provisioning_uri);
        } catch (setupErr) {
          setError(setupErr instanceof ApiError ? setupErr.detail : "Failed to start MFA enrollment");
        }
      } else if (err instanceof ApiError && err.detail.toLowerCase().includes("mfa code")) {
        setNeedMfaCode(true);
        setError("Enter the 6-digit code from your authenticator app.");
      } else {
        setError(err instanceof ApiError ? err.detail : "Login failed. Check your credentials.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function onConfirmEnrollment(event: FormEvent) {
    event.preventDefault();
    if (!enrollmentToken) return;
    setSubmitting(true);
    setError(null);
    try {
      await confirmMfaWithToken(enrollmentToken, enrollCode);
      setEnrollmentToken(null);
      setProvisioningUri(null);
      setNeedMfaCode(true);
      setMfaCode(enrollCode);
      await login(email, password, enrollCode);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "MFA confirmation failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onOidc(provider: string) {
    setError(null);
    try {
      const data = await startOidc(provider);
      window.location.assign(data.authorize_url);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "SSO is not available");
    }
  }

  if (enrollmentToken && provisioningUri) {
    return (
      <div className="login-page">
        <form className="card login-card" onSubmit={onConfirmEnrollment}>
          <h1>Set up MFA</h1>
          <p>Privileged roles require an authenticator app before you can use the platform.</p>
          {error ? <div className="error-banner">{error}</div> : null}
          <p style={{ fontSize: "0.85rem", wordBreak: "break-all" }}>{provisioningUri}</p>
          <div className="form-field">
            <label htmlFor="enroll-code">Authenticator code</label>
            <input
              id="enroll-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={enrollCode}
              onChange={(e) => setEnrollCode(e.target.value)}
              required
              minLength={6}
              maxLength={8}
            />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={submitting}>
            {submitting ? "Confirming…" : "Enable MFA and continue"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="login-page">
      <form className="card login-card" onSubmit={onSubmit}>
        <h1>Andrew Insurance Platform</h1>
        <p>{oidcMfaToken ? "Enter your authenticator code to finish SSO sign-in." : "Sign in with your agency credentials."}</p>
        {error ? <div className="error-banner">{error}</div> : null}
        <div className="form-field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            readOnly={Boolean(oidcMfaToken)}
          />
        </div>
        {!oidcMfaToken ? (
        <div className="form-field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        ) : null}
        {needMfaCode || oidcMfaToken ? (
          <div className="form-field">
            <label htmlFor="mfa">Authenticator code</label>
            <input
              id="mfa"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              required
              minLength={6}
              maxLength={8}
            />
          </div>
        ) : null}
        <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={submitting}>
          {submitting ? "Signing in…" : oidcMfaToken ? "Verify and continue" : "Sign in"}
        </button>
        {!oidcMfaToken && oidcProviders.length > 0 ? (
          <div style={{ marginTop: "1rem", display: "grid", gap: "0.5rem" }}>
            <div className="label" style={{ textAlign: "center" }}>
              Or continue with SSO
            </div>
            {oidcProviders.map((provider) => (
              <button
                key={provider.key}
                type="button"
                className="btn"
                onClick={() => onOidc(provider.key)}
              >
                {provider.label}
              </button>
            ))}
          </div>
        ) : null}
      </form>
    </div>
  );
}
